import { Injectable } from '@angular/core';
import { AppInterface } from '../../interfaces/app.interface';
import { SearchCriteria } from '../../interfaces/search-criteria.interface';
import { SearchableAppInterface } from './searchable-app.interface';
import { FIELD_PRIORITY_WEIGHT, SEARCH_FIELD, SearchPriorityConfig } from '../../interfaces/search-priority-config';

@Injectable({
  providedIn: 'root',
})
export class AppsSearchService {
  search(_apps: AppInterface[], _query: string, _criteria: SearchCriteria[]): AppInterface[] {
  
    const normalizedQuery = this.normalizeQuery(_query);

    if (!normalizedQuery) return _apps;

    if (normalizedQuery.startsWith('#')) {
      const keySearch = normalizedQuery.slice(1).toLowerCase();
      return _apps
        .map((_app) =>
          this.calculateMatchScoreKeyWord(_app as SearchableAppInterface, _app.keywords, keySearch),
        )
        .filter((_app) => _app.totalMatchScore > 0)
        .sort(this.sortByTotalMatchScore)
    }

    const searchQuery = this.getSearchQuery(normalizedQuery);
    
    const searchResult = _apps
      .map((_app) =>
        this.calculateMatchScore(_app as SearchableAppInterface, searchQuery, _criteria),
      )
      .filter((_app) => _app.totalMatchScore > 0)
      
      .sort(this.sortByAppSearchPriority);

      return searchResult
  }

  private normalizeQuery(_query: string): string {
    if (!_query) return '';
    let query = _query.trim();

    if (!query.startsWith('#')) return query;
    query = '#' + query.slice(1).replace(/\s+/g, '');

    return query === '#' ? '' : query;
  }

  private calculateMatchScore(
    _app: SearchableAppInterface,
    _searchQuery: string[],
    _criteria: SearchCriteria[],
  ): SearchableAppInterface {

    let totalMatchScore: number = 0;
    
    let bestPriorityConfig: SearchPriorityConfig = {
      relevanceField: SEARCH_FIELD.OTHER,
      fieldPriorityWeight: FIELD_PRIORITY_WEIGHT.OTHER,
      matchScore: 0
    };

    for (const criteria of _criteria) {

      let criteriaMatchScore = 0;
      
      let currentCriteriaSearchPriorityConfig = {} as SearchPriorityConfig;
      
      for (const _queryPart of _searchQuery) {
        
        const queryPartMatch = criteria.getMatchScore(_app, _queryPart);
        
        currentCriteriaSearchPriorityConfig = criteria.getRelevanceFieldWeightAndMatchScore(_app, _queryPart);

        if (queryPartMatch === 0) {
          criteriaMatchScore = 0;
          break;
        } else {
          criteriaMatchScore = criteriaMatchScore + queryPartMatch;
        }
      }

      if(criteriaMatchScore > 0 && bestPriorityConfig.fieldPriorityWeight < currentCriteriaSearchPriorityConfig.fieldPriorityWeight) {
          bestPriorityConfig = currentCriteriaSearchPriorityConfig;
          bestPriorityConfig.matchScore = criteriaMatchScore;
      }
      
      totalMatchScore = totalMatchScore + criteriaMatchScore;
    
    }
    
    return { 
      ..._app, 
      totalMatchScore, 
      searchPriorityConfig: bestPriorityConfig 
    };
  }

  public sortByAppSearchPriority(a: SearchableAppInterface, b: SearchableAppInterface): number {
    const aConfig = a.searchPriorityConfig;
    const bConfig = b.searchPriorityConfig;

    return (
      bConfig.fieldPriorityWeight - aConfig.fieldPriorityWeight ||
      bConfig.matchScore - aConfig.matchScore
    );
  }

  public sortByTotalMatchScore(a: SearchableAppInterface, b: SearchableAppInterface): number {
    return b.totalMatchScore - a.totalMatchScore;
  }

  private getSearchQuery(_searchQuery: string): string[] {
    return _searchQuery
      .toLowerCase()
      .split(' ')
      .filter((t) => t.length > 0);
  }

  private calculateMatchScoreKeyWord(
    _app: SearchableAppInterface,
    keywords: string[],
    query: string,
  ): SearchableAppInterface {
    const totalMatchScore = keywords.reduce((score, keyword) => {
      if (keyword.toLowerCase().startsWith(query)) {
        score += 2;
      } else if (keyword.toLowerCase().includes(query)) {
        score += 1;
      }
      return score;
    }, 0);
    return { ..._app, totalMatchScore };
  }
}
